import { create } from "zustand";

/**
 * «Специальные возможности» — как в Discord: кегль ленты, воздух между
 * группами сообщений, цвет ролей в именах, подчёркнутые ссылки и меньше
 * движения. Живут на устройстве, как тема и тон фона, и ложатся на корень
 * документа величинами и пометками — правила стилей читают их оттуда.
 */
export type Accessibility = {
  /** Кегль сообщений, px. */
  chatFont: number;
  /** Отступ над новой группой сообщений, px. */
  groupGap: number;
  roleColors: boolean;
  underlineLinks: boolean;
  reduceMotion: boolean;
};

export const ACCESSIBILITY_DEFAULTS: Accessibility = {
  chatFont: 14.5,
  groupGap: 6,
  roleColors: true,
  underlineLinks: false,
  reduceMotion: false,
};

const KEY = "airel.a11y";

function read(): Accessibility {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<Accessibility>;
    return { ...ACCESSIBILITY_DEFAULTS, ...saved };
  } catch {
    return ACCESSIBILITY_DEFAULTS;
  }
}

export const useAccessibility = create<Accessibility>()(() => read());

function apply(state: Accessibility) {
  const root = document.documentElement;
  root.style.setProperty("--chat-font", `${state.chatFont}px`);
  root.style.setProperty("--group-gap", `${state.groupGap}px`);
  root.toggleAttribute("data-plain-names", !state.roleColors);
  root.toggleAttribute("data-underline-links", state.underlineLinks);
  root.toggleAttribute("data-reduce-motion", state.reduceMotion);
}

/** До первой отрисовки — иначе лента моргнёт прежним кеглем. */
export function holdAccessibility() {
  apply(useAccessibility.getState());
  useAccessibility.subscribe(apply);
}

export function setAccessibility(patch: Partial<Accessibility>) {
  useAccessibility.setState(patch);
  try {
    localStorage.setItem(KEY, JSON.stringify(useAccessibility.getState()));
  } catch {
    // Не записалось — выбор доживёт до конца сеанса в памяти.
  }
}

/** «Меньше движения»: своя пометка или такая же просьба системы. */
export const motionReduced = () =>
  useAccessibility.getState().reduceMotion || matchMedia("(prefers-reduced-motion: reduce)").matches;
